"use client";

/**
 * Spis treści — czyta nagłówki h2/h3 z artykułu (.blog-prose),
 * generuje stabilne id, a po kliknięciu płynnie przewija. Aktywny
 * nagłówek jest wyróżniany na podstawie pozycji scrolla.
 *
 * Stworzony jako client-only, bo nasłuchuje DOM po renderze HTML
 * artykułu (który przychodzi z `dangerouslySetInnerHTML`).
 */

import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ListTree } from "lucide-react";

interface Heading {
  id: string;
  text: string;
  level: 2 | 3;
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/ł/g, "l")
    .replace(/ł/g, "l") // safety
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80) || "sekcja";
}

export default function BlogPostToc() {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const observer = useRef<IntersectionObserver | null>(null);

  // Collect headings on mount + assign slugged ids.
  useEffect(() => {
    const article = document.querySelector(".blog-prose");
    if (!article) return;

    const nodes = Array.from(
      article.querySelectorAll("h2, h3")
    ) as HTMLHeadingElement[];

    const seen = new Set<string>();
    const collected: Heading[] = [];
    for (const node of nodes) {
      const text = (node.textContent || "").trim();
      if (!text) continue;
      let id = node.id;
      if (!id) {
        const base = slugify(text);
        id = base;
        let i = 2;
        while (seen.has(id) || document.getElementById(id)) {
          id = `${base}-${i++}`;
        }
        node.id = id;
      }
      // Anchor offset for sticky header (visual padding).
      node.style.scrollMarginTop = "96px";
      seen.add(id);
      collected.push({
        id,
        text,
        level: node.tagName === "H2" ? 2 : 3,
      });
    }
    setHeadings(collected);
  }, []);

  // Highlight active section using IntersectionObserver.
  useEffect(() => {
    if (headings.length === 0) return;
    observer.current?.disconnect();
    observer.current = new IntersectionObserver(
      (entries) => {
        // Wybieramy ostatni nagłówek, który właśnie przeszedł powyżej
        // środka widoku — najbardziej naturalne "aktywne" zaznaczenie.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) =>
              a.target.getBoundingClientRect().top -
              b.target.getBoundingClientRect().top
          );
        if (visible[0]) {
          setActiveId((visible[0].target as HTMLElement).id);
        }
      },
      { rootMargin: "-96px 0px -65% 0px", threshold: [0, 1] }
    );
    for (const h of headings) {
      const el = document.getElementById(h.id);
      if (el) observer.current.observe(el);
    }
    return () => observer.current?.disconnect();
  }, [headings]);

  function handleClick(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
    e.preventDefault();
    const target = document.getElementById(id);
    if (!target) return;
    const top =
      target.getBoundingClientRect().top + window.scrollY - 88;
    window.scrollTo({ top, behavior: "smooth" });
    history.replaceState(null, "", `#${id}`);
    setActiveId(id);
  }

  if (headings.length < 2) return null;

  return (
    <nav
      aria-label="Spis treści"
      className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm"
    >
      <div className="flex items-center gap-2 mb-4">
        <ListTree className="w-5 h-5 text-blue-600" />
        <h3 className="font-semibold text-slate-900">Spis treści</h3>
      </div>
      <ol className="space-y-1 text-sm">
        <AnimatePresence initial={false}>
          {headings.map((h, idx) => {
            const isActive = activeId === h.id;
            return (
              <motion.li
                key={h.id}
                layout
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: Math.min(idx * 0.02, 0.2) }}
                className={h.level === 3 ? "ml-4" : ""}
              >
                <a
                  href={`#${h.id}`}
                  onClick={(e) => handleClick(e, h.id)}
                  className={[
                    "relative block rounded-md py-1.5 pl-3 pr-2 leading-snug transition-colors",
                    isActive
                      ? "text-blue-700 bg-blue-50 font-semibold"
                      : "text-slate-600 hover:text-blue-600 hover:bg-slate-50",
                  ].join(" ")}
                >
                  <span
                    aria-hidden
                    className={[
                      "absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full transition-all",
                      isActive ? "bg-blue-500" : "bg-transparent",
                    ].join(" ")}
                  />
                  {h.text}
                </a>
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ol>
    </nav>
  );
}
